import React from "react";
import {
  FaCheckCircle,
  FaCrown,
  FaHeart,
  FaShieldAlt,
  FaInfinity,
  FaLockOpen,
  FaRandom,
  FaGamepad,
  FaMicrophone,
  FaVideo,
  FaListUl,
  FaEye,
  FaLock,
} from "react-icons/fa";
import { useSubscription } from "../../contexts/SubscriptionContext";
import PlanCards from "./PlanCards";
import { Button, Modal } from "../../ui";

// Premium feature grid, mirroring the mobile paywall (icon tile + title + sub).
const FEATURES = [
  {
    Icon: FaInfinity,
    title: "إنشاء فئات غير محدودة",
    sub: "أنشئ فئاتك الخاصة بدون حدود",
  },
  {
    Icon: FaLockOpen,
    title: "جميع الفئات الرسمية",
    sub: "افتح جميع فئات تربع المميزة",
  },
  {
    Icon: FaRandom,
    title: "الاختيار العشوائي",
    sub: "اختر فئات عشوائية لكل لعبة",
  },
  {
    Icon: FaGamepad,
    title: "الألعاب الجاهزة المميزة",
    sub: "العب ألعاب جاهزة حصرية",
  },
  {
    Icon: FaMicrophone,
    title: "أسئلة صوتية غير محدودة",
    sub: "أنشئ وشارك أسئلة صوتية دون قيود",
  },
  {
    Icon: FaVideo,
    title: "أسئلة فيديو غير محدودة",
    sub: "تسجيل ومشاركة مقاطع فيديو للفئات",
  },
  {
    Icon: FaListUl,
    title: "أسئلة اختيار متعدد",
    sub: "إنشاء أسئلة متقدمة متعددة الخيارات",
  },
  {
    Icon: FaEye,
    title: "مشاهدة أسئلة فئات المجتمع",
    sub: "اطّلع على أسئلة الفئات المنشورة",
  },
  {
    Icon: FaLock,
    title: "فئات خاصة",
    sub: "أنشئ فئات خاصة لك ولأصدقائك فقط",
  },
  {
    Icon: FaCrown,
    title: "مميزات حصرية إضافية",
    sub: "صور فِرَق، تخصيص الحساب، والمزيد",
  },
];

const FeatureList = () => (
  <ul className="grid gap-2 sm:grid-cols-2">
    {FEATURES.map((f, i) => (
      <li
        key={i}
        className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 p-2.5"
      >
        <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <f.Icon size={17} aria-hidden="true" />
        </span>
        <div className="flex-1 min-w-0 text-right">
          <div className="text-white text-sm font-bold">{f.title}</div>
          <div className="text-white/55 text-xs leading-snug">{f.sub}</div>
        </div>
      </li>
    ))}
  </ul>
);

// The subscription sheet, matching the mobile paywall: crown header, support
// note, premium feature grid, plan cards, and a secure-payment footer.
// Premium users see their status instead.
const SubscriptionModal = ({ open, onClose }) => {
  const { isPremium, subscription, promoSubscription } = useSubscription();
  if (!open) return null;
  const active = subscription || promoSubscription;

  return (
    <Modal open={open} onClose={onClose} size="lg" labelledBy="subscription-title">
      {/* Header */}
      <div className="text-center -mt-4 mb-6">
        <span className="mx-auto mb-3 w-16 h-16 rounded-2xl bg-primary/15 border border-primary/30 text-primary flex items-center justify-center shadow-[0_0_32px_-6px_rgb(var(--c-primary)/0.6)]">
          <FaCrown size={30} aria-hidden="true" />
        </span>
        <h2 id="subscription-title" className="text-2xl font-extrabold text-primary">
          ترَبَّع بريميوم
        </h2>
        {isPremium ? (
          <p className="mt-2 inline-flex items-center gap-2 text-success text-sm font-bold">
            <FaCheckCircle aria-hidden="true" /> أنت مشترك في بريميوم
          </p>
        ) : (
          <p className="text-white/70 text-sm mt-1">
            افتح جميع المميزات وادعم تطوير التطبيق
          </p>
        )}
        {!isPremium && (
          <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-mainButton/30 bg-mainButton/10 px-3.5 py-2.5 text-right">
            <FaHeart className="text-lightBrown shrink-0" aria-hidden="true" />
            <span className="text-white/85 text-xs leading-relaxed">
              اشتراكك يدعم تطوير التطبيق وبقاءه وتحديثه بميزات جديدة
            </span>
          </div>
        )}
      </div>

      {isPremium ? (
        <div>
          {active?.expiryDate && (
            <p className="text-white/70 text-center mb-5">
              ينتهي اشتراكك في{" "}
              <span className="font-bold text-white">
                {new Date(active.expiryDate).toLocaleDateString("ar", {
                  numberingSystem: "latn",
                })}
              </span>
            </p>
          )}
          <h3 className="font-bold mb-3">مميزاتك المفعّلة</h3>
          <FeatureList />
          <Button variant="secondary" block className="mt-6" onClick={onClose}>
            تم
          </Button>
        </div>
      ) : (
        <>
          <h3 className="font-bold mb-3">مميزات البريميوم</h3>
          <div className="mb-6">
            <FeatureList />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
            <h3 className="font-bold mb-4">اختر باقتك</h3>
            <PlanCards />
          </div>

          <div className="mt-5 flex flex-col items-center gap-2.5">
            <div className="flex items-center gap-2 rounded-full bg-success/10 border border-success/30 px-3 py-1.5">
              <FaShieldAlt className="text-success" size={13} aria-hidden="true" />
              <span className="text-success text-xs font-semibold">
                دفع آمن ومشفّر عبر ماي فاتورة
              </span>
            </div>
            <p className="text-white/55 text-xs text-center leading-relaxed">
              دفعة واحدة لكامل المدة، بدون تجديد تلقائي.
            </p>
          </div>
        </>
      )}
    </Modal>
  );
};

export default SubscriptionModal;
